import * as objectUtils from '../object-utils';
import { getUseDataAccessAmplifyCapability } from '../amplify/utils/bench-editable';
import { isEmpty, capitalize, upperFirst, truncate } from '../object-utils';
const _lookupTable = new Map<string, string>([
  ['body', 'raw_body'],
  ['reason_for_visit', 'reason_for_visit'],
  ['bodyPart', 'body_region']
]);
export const getFieldId = (fieldName: string) => {
  return _lookupTable.get(fieldName) || fieldName;
};

/**
 * Formats a given text based on specific conditions.
 *
 * @param {string} text - The input text that needs to be formatted.
 * @param {number} [max_length=20] - The maximum allowed length for the text before truncation. Default value is 20.
 * @returns {string} - Returns the formatted string where:
 *   - If the text contains a comma, it treats the string as "LastName, FirstName".
 *     It capitalizes the last name and keeps only the first letter of the first name.
 *   - If the text does not contain a comma, it is truncated to the specified `max_length`
 *     and capitalized accordingly.
 *   - If the input text is empty, it returns an empty string.
 *
 * Example:
 *   formatDisplayText('Doe, John'); // Returns: 'Doe, J'
 *   formatDisplayText('apple banana', 5); // Returns: 'Apple'
 */
export const formatDisplayText = (text: string, max_length: number = 20) => {
  if (isEmpty(text)) return '';

  if (text.includes(',')) {
    const [lastName, firstName] = text.split(',').map(item => item.trim());
    return (
      capitalize(lastName) +
      (isEmpty(firstName) ? '' : `, ${upperFirst(firstName[0])}`)
    );
  }
  return upperFirst(truncate(text, max_length, '').toLowerCase());
};

export const doesPathEndWithV2 = (): boolean => {
  return window.location.pathname.endsWith('/v2/');
};

export const buildDataAccessRequestObject = (state: any, name: string,
  actionDestination: string): CMxCommonApp.DataAccessRequestObject => {
  const context = state?.codeBench?.userContext || null;
  const activeUser = state?.ui?.activeUser || null;
  const serviceLine = state?.ui?.serviceLine?.name || "";

  return {
    sourceId: activeUser?.userId ?? "",
    serviceLine: serviceLine,
    healthSystemUid: context?.healthSystemTenantId ?? "",
    tenantUid: context?.tenantId ?? "",
    name,
    action: "read",
    actionDestination,
    sort: "Asc",
    limit: 1000,
  };
};

export const buildPatienttimelineDataAccessRequest = (
  ui: any,
  name: string,
  actionDestination: string
): CMxCommonApp.DataAccessRequestObject => {
  const context = ui?.context?.activeContext || null;
  const activeUser = ui?.activeUser || null;
  const serviceLine = ui?.serviceLine?.name || '';

  return {
    sourceId: activeUser?.userId ?? '',
    serviceLine: serviceLine,
    healthSystemUid: context?.healthSystemTenantId ?? '',
    tenantUid: context?.tenantId ?? '',
    name,
    action: 'read',
    actionDestination,
    sort: 'Asc',
    limit: 1000
  };
};

/**
 * Determines whether to fetch provider data from the Data Access API
 * based on the capability returned by getUsedataaccessAmplifyCapability.
 *
 * @param state - Redux or component state containing userContext and serviceLine
 * @returns boolean indicating whether Data Access API should be used
 */
export const shouldFetchFromDataAccess = (state: any): boolean => {
  const capability = getUseDataAccessAmplifyCapability(
    state?.codeBench?.userContext,
    state?.ui?.serviceLine?.name
  );

  // Ensure we safely return a boolean
  return Boolean(capability?.on?.value);
};

/**
 * Determines whether to fetch provider data from the Data Access API
 * based on the capability returned by getUsedataaccessAmplifyCapability.
 *
 * @param state - Redux or component state containing userContext and serviceLine
 * @returns boolean indicating whether Data Access API should be used
 */
export const getUseDataAccessCapabilityByContextAndServiceLine = (
  state: any
): boolean => {
  const capability = getUseDataAccessAmplifyCapability(
    state?.codeBench?.userContext,
    state?.ui?.serviceLine?.name
  );

  // Ensure we safely return a boolean
  return Boolean(capability?.on?.value);
};


export const getAvatarInitials = (firstName: string, lastName: string) => {
  if (objectUtils.isEmpty(firstName) && objectUtils.isEmpty(lastName)) {
    return 'N/A';
  }
  // Get the initials
  var initials =
    firstName.charAt(0).toUpperCase() +
    (lastName && !objectUtils.isEmpty(lastName)
      ? ` ${lastName.charAt(0).toUpperCase()}`
      : '');
  return initials;
};



